import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  StyleSheet,
  ScrollView,
  Text,
  TouchableOpacity,
  View as RNView,
  Modal,
  Pressable,
  ActivityIndicator,
  TextInput,
  Platform,
  KeyboardAvoidingView,
  FlatList,
  useWindowDimensions,
} from "react-native";
import FeatherIcon from "react-native-vector-icons/Feather";
import { useDispatch, useSelector } from "react-redux";
import {
  getAppointmentsByDate,
  getSalesStates,
  getWarehouseList,
} from "../../../redux/dispatcher/portalDispatcher";
import {
  resetSalesProductFilters,
  setSalesLocationFilter,
  setSalesStateFilter,
} from "../../../redux/reducers/portalReducer";
import { useAppTheme } from "../../../hooks/useAppTheme";
import { ThemeColors } from "../../../utils/constants/colors";

// ─── Design Tokens ────────────────────────────────────────────────────────────

const getC = (colors: ThemeColors, isDark: boolean) => ({
  accent: colors.primary,
  success: colors.success,
  warning: colors.warning,
  warningBg: isDark ? "rgba(245, 158, 11, 0.2)" : "#FFF8EC",
  danger: colors.danger,
  dangerBg: isDark ? "rgba(231, 76, 60, 0.2)" : "#FEF0EE",
  surface: colors.surface,
  surfaceAlt: isDark ? colors.surfaceSubtle : "#F2F2F2",
  field: isDark ? colors.surfaceSubtle : "#EEF2F8",
  cell: isDark ? colors.surfaceSubtle : "#E4E4E4",
  rowAlt: isDark ? colors.surfaceSubtle : "#FAFAFA",
  border: colors.borderSubtle,
  frame: colors.primary,
  text: colors.text,
  textMid: colors.textSecondary,
  textMuted: colors.textMuted,
  overlay: "rgba(10,20,35,0.5)",
});

type Tokens = ReturnType<typeof getC>;

const useTokens = () => {
  const { colors, isDark } = useAppTheme();
  return useMemo(() => getC(colors, isDark), [colors, isDark]);
};

/** Dialog vs full screen, and centred pickers vs bottom sheets */
const WIDE_BREAKPOINT = 700;
const webNoOutline =
  Platform.OS === "web" ? ({ outlineStyle: "none" } as any) : null;

// ─── Types ────────────────────────────────────────────────────────────────────

type Uom = "Boxes" | "Pieces" | "Pallets";
const UOMS: Uom[] = ["Pieces", "Boxes", "Pallets"];
const REQUEST_TYPES = ["Request Delivery", "Pick up"] as const;
type RequestType = (typeof REQUEST_TYPES)[number];

interface PriceEntry {
  price: number;
  source?: "default" | "sales" | "default_fallback" | string;
  priceListDefaultId?: string | null;
  priceListSalesId?: string | null;
}

export interface ProductData {
  UniqueKey: string;
  ["Portal Item Name"]: string;
  ItemCodes?: string[];
  PerCtns?: number;
  PerPallet?: number;
  PicturName?: string | null;
  /** Shown in the State column */
  State?: unknown;
  /** Shown in the Location column — a string, a list, or objects with a name */
  Location?: unknown;
  /** Optional – shown in the Available (Pieces) column */
  Available2?: number | string | null;
  pricelist?: {
    priceListId?: string;
    prices?: Record<string, PriceEntry | undefined>;
  } | null;
}

export interface SalesCustomer {
  _id: string;
  name: string;
  cardCode?: string[] | string;
  pricingEnabled?: boolean;
  pricingModel?: "default" | "sales";
  pricingSalesEmail?: string | null;
  requiredDateLeadDays?: number;
  contacts?: { address: string }[];
}

export interface Grower {
  _id: string;
  name: string;
  address?: string[];
  /** Warehouse whose calendar is used for pickups */
  defaultWarehouse?: string | null;
  allowAppointmentsPrePickup?: boolean;
}

/** Pickup warehouse — its calendar holds the pickup appointments */
export interface Warehouse {
  _id: string;
  name: string;
  calendarId: string;
  timezone?: string | null;
}

/** An existing booking, used to grey out taken pickup slots */
export interface BookedSlot {
  _id?: string;
  startDateTime: string;
  endDateTime: string;
}

type Ref = string | { _id?: string } | null | undefined;

interface ExistingAppointment {
  _id?: string;
  startDateTime?: string;
  endDateTime?: string;
}

/** An order to edit — the shape the org orders list / order summary already has */
export interface EditableOrder {
  _id: string;
  orderNo?: string;
  organization?: Ref;
  subOrganization?: Ref;
  requestType?: string;
  poNumber?: string;
  remarks?: string;
  appointments?: ExistingAppointment[];
  details?: {
    uniqueKey?: string;
    itemCodes?: string[];
    itemName?: string;
    pictureName?: string | null;
    quantity?: number;
    uom?: string;
    perCtns?: number;
    perPallet?: number;
    requiredDate?: string;
    deliveryTo?: string;
    unitPrice?: number | null;
    appointments?: ExistingAppointment[];
  }[];
}

/** Per-product row state in the inventory table */
interface RowState {
  selected: boolean;
  qtyText: string;
  uom: Uom;
  priceText: string;
  priceEdited: boolean; // false → follow the price list when UOM changes
}

export interface SalesOrderPayload {
  organization: string;
  subOrganization: string | null;
  requestType: RequestType;
  poNumber: string;
  remarks: string;
  /**
   * Pick up orders only — same top-level shape as the customer createOrder,
   * which is what createAppointmentHelper reads. Wall time in `timezone`.
   */
  startDateTime?: string | null; // "YYYY-MM-DDTHH:mm:ss"
  endDateTime?: string | null;
  timezone?: string | null;
  details: {
    uniqueKey: string;
    itemCodes: string[];
    itemName: string;
    pictureName: string | null;
    quantity: number;
    uom: Uom;
    perCtns: number;
    perPallet: number;
    requiredDate: string;
    deliveryTo?: string;
    /** Pickup warehouse (grower's default) — the appointment is booked on its calendar */
    warehouse?: string | null;
    timezone?: string | null;
    /** Rep-entered price. Backend creates the price list entry if missing, else updates it. */
    unitPrice: number;
    totalPrice: number;
  }[];
}

interface CreateSalesOrderModalProps {
  visible: boolean;
  onClose: () => void;
  /** Customers where the rep is on org.sales[] */
  loadCustomers: () => Promise<SalesCustomer[]>;
  loadGrowers: (organizationId: string) => Promise<Grower[]>;
  /**
   * Products for the customer. The thunk adds the State / Location filters
   * from `state.portal.admin.salesOrders`, so set those before calling.
   */
  loadProducts: (customer: SalesCustomer) => Promise<ProductData[]>;
  /** POST /orders/sales */
  onSubmit: (payload: SalesOrderPayload) => Promise<void>;
  /** Open with this customer selected and locked */
  initialCustomerId?: string | null;
  /**
   * Edit mode: prefill from this order. The customer is locked to the order's,
   * and onSubmit should call the update API instead of create.
   */
  editOrder?: EditableOrder | null;
  /** Fallback time zone when the warehouse has none */
  appointmentTimeZone?: string;
  businessHours?: { startHour: number; endHour: number };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const round4 = (n: number) => parseFloat(n.toFixed(4));
/** Any API value → display string ("" for null/undefined; objects use name/code/label) */
const str = (v: unknown): string => {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (Array.isArray(v)) return v.map(str).filter(Boolean).join(", ");
  if (typeof v === "object") {
    const o = v as Record<string, unknown>;
    return str(o.name ?? o.code ?? o.label ?? o.value ?? "");
  }
  return String(v);
};
const lc = (v: unknown) => str(v).trim().toLowerCase();
const GST_RATE = 0.1;
const money = (n: number) =>
  `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const priceEntryFor = (
  pl: ProductData["pricelist"],
  uom: Uom,
): PriceEntry | null =>
  pl?.prices?.[uom] ?? pl?.prices?.[uom.toUpperCase()] ?? null;

const listPriceText = (p: ProductData, uom: Uom) => {
  const e = priceEntryFor(p.pricelist, uom);
  return e?.price != null ? String(e.price) : "";
};

const parsePrice = (text: string): number | null => {
  if (text.trim() === "") return null;
  const n = Number(text);
  return Number.isFinite(n) && n >= 0 ? round4(n) : null;
};

const parseQty = (text: string) => {
  const n = parseInt(text, 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const toPieces = (qty: number, uom: Uom, perCtns: number, perPallet: number) =>
  uom === "Pieces"
    ? qty
    : uom === "Boxes"
      ? qty * (perCtns || 1)
      : qty * (perPallet || 1) * (perCtns || 1);

const defaultRow = (p: ProductData): RowState => ({
  selected: false,
  qtyText: "",
  uom: "Pieces",
  priceText: listPriceText(p, "Pieces"),
  priceEdited: false,
});

// ── Dates (local calendar days as "YYYY-MM-DD") ────────────────────────────

const pad = (n: number) => String(n).padStart(2, "0");
const toIso = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fromIso = (s: string) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const addDays = (d: Date, n: number) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
const isWeekend = (d: Date) => d.getDay() === 0 || d.getDay() === 6;
const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};
/** First weekday on or after `d` */
const nextWeekday = (d: Date) => {
  let x = new Date(d);
  while (isWeekend(x)) x = addDays(x, 1);
  return x;
};
const longDate = (iso?: string) =>
  iso
    ? fromIso(iso).toLocaleDateString(undefined, {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "";

// ── Pickup slots ────────────────────────────────────────────────────────────

const SLOT_MINUTES = 30;
const DEFAULT_BUSINESS = { startHour: 8, endHour: 16 };

const timeLabel = (hhmm: string) => {
  const [h, m] = hhmm.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  return `${((h + 11) % 12) + 1}:${pad(m)} ${ampm}`;
};

const addMinutes = (hhmm: string, mins: number) => {
  const [h, m] = hhmm.split(":").map(Number);
  const t = h * 60 + m + mins;
  return `${pad(Math.floor(t / 60))}:${pad(t % 60)}`;
};

/** Whole local day for a "YYYY-MM-DD" */
const dayRange = (iso: string) => {
  const start = fromIso(iso);
  const end = new Date(start);
  end.setHours(23, 59, 59, 999);
  return { start, end };
};

type LoadStatus = "loading" | "done" | "error";
const NO_BOOKINGS: BookedSlot[] = [];

interface Slot {
  start: string; // "HH:mm"
  label: string;
  disabled: boolean;
}

const buildSlots = (
  dateIso: string,
  hours: { startHour: number; endHour: number },
  booked: BookedSlot[],
): Slot[] => {
  const day = fromIso(dateIso);
  const now = new Date();
  const out: Slot[] = [];
  for (
    let mins = hours.startHour * 60;
    mins < hours.endHour * 60;
    mins += SLOT_MINUTES
  ) {
    const start = `${pad(Math.floor(mins / 60))}:${pad(mins % 60)}`;
    const s = new Date(day);
    s.setHours(Math.floor(mins / 60), mins % 60, 0, 0);
    const e = new Date(s.getTime() + SLOT_MINUTES * 60000);
    const taken = booked.some(
      (b) => s < new Date(b.endDateTime) && e > new Date(b.startDateTime),
    );
    out.push({ start, label: timeLabel(start), disabled: s < now || taken });
  }
  return out;
};

// ── Edit mode ───────────────────────────────────────────────────────────────

const idOf = (v: Ref) =>
  v == null ? "" : typeof v === "string" ? v : (v._id ?? "");

/** Stored dates come back as ISO strings; the calendar day is the first 10 chars */
const dayOf = (v?: string | null) => (v ? String(v).slice(0, 10) : "");

/** An instant → { date: "YYYY-MM-DD", time: "HH:mm" } as wall time in `timeZone` */
const wallTimeIn = (iso: string, timeZone: string) => {
  const d = new Date(iso);
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(d);
    const get = (t: string) => parts.find((x) => x.type === t)?.value ?? "";
    return {
      date: `${get("year")}-${get("month")}-${get("day")}`,
      time: `${get("hour")}:${get("minute")}`,
    };
  } catch {
    // No Intl time-zone support: fall back to device time
    return {
      date: toIso(d),
      time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
    };
  }
};

/** The order's own appointments (order level and per detail), de-duplicated */
const ownAppointments = (o?: EditableOrder | null): ExistingAppointment[] => {
  if (!o) return [];
  const all = [
    ...(o.appointments ?? []),
    ...(o.details ?? []).flatMap((d) => d.appointments ?? []),
  ].filter((a) => a?.startDateTime);
  const seen = new Set<string>();
  return all.filter((a) => {
    const k = a._id ?? a.startDateTime!;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
};

/** Any API value → a list of display strings (for multi-value cells) */
const listOf = (v: unknown): string[] =>
  v == null
    ? []
    : Array.isArray(v)
      ? [...new Set(v.flatMap(listOf))]
      : [str(v)].map((x) => x.trim()).filter(Boolean);

// Table column widths — minimums; the table stretches to fill wide screens
// (extra width goes mostly to Location, then State and Item name)
// and scrolls horizontally on narrow ones
const COL = {
  check: 48,
  state: 130,
  location: 200,
  code: 150,
  name: 220,
  available: 120,
  qty: 110,
  uom: 110,
  price: 130,
  total: 120,
};
const TABLE_WIDTH = Object.values(COL).reduce((a, b) => a + b, 0) + 16;

// ─── Small building blocks ────────────────────────────────────────────────────

/** Small "Label: value ▾" button for the inventory filters */
const FilterChip = ({
  label,
  value,
  active,
  busy,
  disabled,
  onPress,
  C,
}: {
  label: string;
  value: string;
  active: boolean;
  busy?: boolean;
  disabled?: boolean;
  onPress: () => void;
  C: Tokens;
}) => {
  const S = createS(C);
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityLabel={`${label}: ${value}`}
      style={(s: any) => [
        S.filterChip,
        active && S.filterChipActive,
        (s.hovered || s.pressed) &&
          !active && { backgroundColor: C.surfaceAlt },
        disabled && { opacity: 0.5 },
      ]}
    >
      <Text style={S.filterChipLabel}>{label}</Text>
      <Text
        style={[S.filterChipValue, active && { color: C.accent }]}
        numberOfLines={1}
      >
        {value}
      </Text>
      {busy ? (
        <ActivityIndicator size="small" color={C.textMuted} />
      ) : (
        <FeatherIcon name="chevron-down" size={13} color={C.textMuted} />
      )}
    </Pressable>
  );
};

/** Table cell showing every value as a wrapping chip ("-" when empty) */
const ChipList = ({
  values,
  style,
  S,
}: {
  values: string[];
  style?: any;
  S: ReturnType<typeof createS>;
}) => (
  <RNView style={[S.locCell, style]}>
    {values.length ? (
      values.map((v) => (
        <RNView key={v} style={S.locChip}>
          <Text style={S.locChipText}>{v}</Text>
        </RNView>
      ))
    ) : (
      <Text style={S.td}>-</Text>
    )}
  </RNView>
);

const Field = ({
  label,
  width,
  error,
  hint,
  children,
  C,
}: {
  label: string;
  width: any;
  error?: string;
  hint?: string;
  children: React.ReactNode;
  C: Tokens;
}) => {
  const S = createS(C);
  return (
    <RNView style={{ width, marginBottom: 16 }}>
      <Text style={S.fieldLabel}>{label}</Text>
      {children}
      {!!error ? (
        <RNView style={S.errorRow}>
          <FeatherIcon name="alert-circle" size={11} color={C.danger} />
          <Text style={S.errorText}>{error}</Text>
        </RNView>
      ) : hint ? (
        <Text style={S.hintText}>{hint}</Text>
      ) : null}
    </RNView>
  );
};

const SelectBox = ({
  value,
  placeholder,
  onPress,
  disabled,
  error,
  locked,
  icon,
  C,
}: {
  value?: string | null;
  placeholder?: string;
  onPress: () => void;
  disabled?: boolean;
  error?: boolean;
  locked?: boolean;
  icon?: string;
  C: Tokens;
}) => {
  const S = createS(C);
  return (
    <Pressable
      style={(s: any) => [
        S.box,
        s.hovered && !disabled && !locked && { borderColor: C.accent + "88" },
        error && { borderColor: C.danger },
        disabled && { opacity: 0.55 },
      ]}
      onPress={onPress}
      disabled={disabled || locked}
    >
      <Text
        style={[S.boxText, !value && { color: C.textMuted }]}
        numberOfLines={1}
      >
        {value || placeholder || ""}
      </Text>
      <FeatherIcon
        name={locked ? "lock" : (icon ?? "chevron-down")}
        size={locked || icon ? 14 : 17}
        color={locked || icon ? C.textMuted : C.text}
      />
    </Pressable>
  );
};

// ─── Responsive sheet: centred dialog on wide screens, bottom sheet on phones ─

const Sheet = ({
  title,
  onClose,
  maxWidth = 440,
  children,
  C,
}: {
  title: string;
  onClose: () => void;
  maxWidth?: number;
  children: React.ReactNode;
  C: Tokens;
}) => {
  const SRP = createSRP(C);
  const { width } = useWindowDimensions();
  const wide = width >= WIDE_BREAKPOINT;
  return (
    <Modal
      visible
      transparent
      animationType={wide ? "fade" : "slide"}
      onRequestClose={onClose}
    >
      <RNView
        style={[SRP.backdrop, wide ? SRP.backdropCenter : SRP.backdropBottom]}
      >
        <Pressable
          style={StyleSheet.absoluteFill}
          onPress={onClose}
          accessibilityLabel="Close"
        />
        <RNView
          style={[SRP.panel, wide ? [SRP.dialog, { maxWidth }] : SRP.sheet]}
        >
          {!wide && <RNView style={SRP.handle} />}
          <RNView style={SRP.titleRow}>
            <Text style={SRP.title}>{title}</Text>
            <Pressable
              onPress={onClose}
              hitSlop={8}
              accessibilityLabel="Close"
              style={(s: any) => [
                SRP.closeBtn,
                (s.hovered || s.pressed) && { backgroundColor: C.surfaceAlt },
              ]}
            >
              <FeatherIcon name="x" size={18} color={C.textMuted} />
            </Pressable>
          </RNView>
          {children}
        </RNView>
      </RNView>
    </Modal>
  );
};

interface SheetOption {
  value: string;
  label: string;
  sub?: string;
}

const OptionSheet = ({
  title,
  options,
  selected,
  searchable,
  emptyText,
  onSelect,
  onClose,
  C,
}: {
  title: string;
  options: SheetOption[];
  selected?: string | null;
  searchable?: boolean;
  emptyText?: string;
  onSelect: (value: string) => void;
  onClose: () => void;
  C: Tokens;
}) => {
  const SRP = createSRP(C);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = lc(query);
    if (!q) return options;
    return options.filter(
      (o) => lc(o.label).includes(q) || lc(o.sub).includes(q),
    );
  }, [options, query]);

  return (
    <Sheet title={title} onClose={onClose} C={C}>
      {searchable && (
        <RNView style={SRP.searchWrap}>
          <FeatherIcon name="search" size={14} color={C.textMuted} />
          <TextInput
            style={[SRP.searchInput, webNoOutline]}
            value={query}
            onChangeText={setQuery}
            placeholder="Search…"
            placeholderTextColor={C.textMuted}
            autoCorrect={false}
            autoFocus={Platform.OS === "web"}
          />
        </RNView>
      )}

      <FlatList
        style={{ flexGrow: 0 }}
        data={filtered}
        keyExtractor={(o) => o.value}
        keyboardShouldPersistTaps="handled"
        ListEmptyComponent={
          <Text style={SRP.empty}>{emptyText ?? "Nothing found"}</Text>
        }
        renderItem={({ item }) => {
          const on = selected === item.value;
          return (
            <Pressable
              style={(s: any) => [
                SRP.option,
                (s.hovered || s.pressed) && SRP.optionHover,
                on && SRP.optionActive,
              ]}
              onPress={() => {
                onSelect(item.value);
                onClose();
              }}
            >
              <RNView style={{ flex: 1 }}>
                <Text style={[SRP.optionText, on && { color: C.accent }]}>
                  {item.label}
                </Text>
                {!!item.sub && (
                  <Text style={SRP.optionSub} numberOfLines={1}>
                    {item.sub}
                  </Text>
                )}
              </RNView>
              {on && <FeatherIcon name="check" size={16} color={C.accent} />}
            </Pressable>
          );
        }}
      />
    </Sheet>
  );
};

// ─── Calendar ─────────────────────────────────────────────────────────────────

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const CalendarSheet = ({
  title,
  value,
  minDate,
  onSelect,
  onClose,
  note,
  isDayFull,
  C,
}: {
  title: string;
  value?: string;
  minDate: Date;
  onSelect: (iso: string) => void;
  onClose: () => void;
  note?: string;
  /** Fully booked days (already fetched) are disabled and marked "Full" */
  isDayFull?: (iso: string) => boolean;
  C: Tokens;
}) => {
  const K = createK(C);
  const initial = value ? fromIso(value) : minDate;
  const [month, setMonth] = useState(
    new Date(initial.getFullYear(), initial.getMonth(), 1),
  );

  const minMonth = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
  const canPrev = month > minMonth;
  const todayIso = toIso(startOfToday());

  // 6 rows × 7 days, weeks start on Monday
  const cells = useMemo(() => {
    const first = new Date(month);
    const lead = (first.getDay() + 6) % 7;
    const start = addDays(first, -lead);
    return Array.from({ length: 42 }, (_, i) => addDays(start, i));
  }, [month]);

  const shift = (n: number) =>
    setMonth(new Date(month.getFullYear(), month.getMonth() + n, 1));

  return (
    <Sheet title={title} onClose={onClose} maxWidth={380} C={C}>
      <RNView style={K.monthRow}>
        <Pressable
          onPress={() => shift(-1)}
          disabled={!canPrev}
          accessibilityLabel="Previous month"
          style={(s: any) => [
            K.navBtn,
            (s.hovered || s.pressed) &&
              canPrev && { backgroundColor: C.surfaceAlt },
            !canPrev && { opacity: 0.3 },
          ]}
        >
          <FeatherIcon name="chevron-left" size={18} color={C.text} />
        </Pressable>
        <RNView style={K.monthCenter}>
          <Text style={K.monthText}>
            {month.toLocaleDateString(undefined, {
              month: "long",
              year: "numeric",
            })}
          </Text>
        </RNView>
        <Pressable
          onPress={() => shift(1)}
          accessibilityLabel="Next month"
          style={(s: any) => [
            K.navBtn,
            (s.hovered || s.pressed) && { backgroundColor: C.surfaceAlt },
          ]}
        >
          <FeatherIcon name="chevron-right" size={18} color={C.text} />
        </Pressable>
      </RNView>

      <RNView style={K.grid}>
        {WEEKDAYS.map((w) => (
          <Text
            key={w}
            style={[
              K.weekday,
              (w === "Sat" || w === "Sun") && { opacity: 0.5 },
            ]}
          >
            {w}
          </Text>
        ))}
        {cells.map((d) => {
          const iso = toIso(d);
          const inMonth = d.getMonth() === month.getMonth();
          const blocked = d < minDate || isWeekend(d);
          const full = !blocked && inMonth && !!isDayFull?.(iso);
          const disabled = blocked || full;
          const on = iso === value;
          return (
            <RNView key={iso} style={K.cell}>
              <Pressable
                onPress={() => {
                  onSelect(iso);
                  onClose();
                }}
                disabled={disabled}
                accessibilityLabel={`${longDate(iso)}${full ? ", fully booked" : ""}`}
                accessibilityState={{ disabled, selected: on }}
                style={(s: any) => [
                  K.day,
                  iso === todayIso && K.today,
                  (s.hovered || s.pressed) &&
                    !disabled &&
                    !on && { backgroundColor: C.surfaceAlt },
                  on && K.daySelected,
                ]}
              >
                <Text
                  style={[
                    K.dayText,
                    !inMonth && { opacity: 0.35 },
                    disabled && K.dayDisabled,
                    full && K.dayFullText,
                    on && { color: "#fff", fontWeight: "800" },
                  ]}
                >
                  {d.getDate()}
                </Text>
                {full && <Text style={K.fullTag}>Full</Text>}
              </Pressable>
            </RNView>
          );
        })}
      </RNView>

      <RNView style={K.footer}>
        <FeatherIcon name="info" size={12} color={C.textMuted} />
        <Text style={K.footerText}>
          {note ?? `Weekdays only, from ${longDate(toIso(minDate))}`}
        </Text>
        {!!isDayFull && (
          <RNView style={K.legend}>
            <RNView style={K.legendDot} />
            <Text style={K.footerText}>Full</Text>
          </RNView>
        )}
      </RNView>
    </Sheet>
  );
};

// ─── Loader overlay (shown until every load has finished) ─────────────────────

type StepState = "pending" | "loading" | "done";

const LoadingOverlay = ({
  steps,
  failed,
  message,
  onRetry,
  onClose,
  C,
}: {
  steps: { label: string; state: StepState }[];
  failed: boolean;
  message?: string | null;
  onRetry: () => void;
  onClose: () => void;
  C: Tokens;
}) => {
  const L = createL(C);
  return (
    <RNView style={L.overlay}>
      <RNView style={L.card}>
        {failed ? (
          <RNView style={[L.iconWrap, { backgroundColor: C.dangerBg }]}>
            <FeatherIcon name="wifi-off" size={22} color={C.danger} />
          </RNView>
        ) : (
          <ActivityIndicator size="large" color={C.accent} />
        )}

        <Text style={L.title}>
          {failed ? "Couldn't load order data" : "Preparing order form"}
        </Text>
        <Text style={L.sub}>
          {failed
            ? message ||
              "Something went wrong while loading. Check your connection and try again."
            : "Fetching customers, growers and live pricing…"}
        </Text>

        <RNView style={L.steps}>
          {steps.map((s) => (
            <RNView key={s.label} style={L.step}>
              {s.state === "done" ? (
                <FeatherIcon name="check-circle" size={15} color={C.success} />
              ) : s.state === "loading" && !failed ? (
                <ActivityIndicator size="small" color={C.accent} />
              ) : (
                <FeatherIcon
                  name={failed && s.state !== "pending" ? "x-circle" : "circle"}
                  size={15}
                  color={
                    failed && s.state !== "pending" ? C.danger : C.textMuted
                  }
                />
              )}
              <Text
                style={[L.stepText, s.state === "done" && { color: C.text }]}
              >
                {s.label}
              </Text>
            </RNView>
          ))}
        </RNView>

        {failed && (
          <RNView style={L.actions}>
            <TouchableOpacity style={L.secondaryBtn} onPress={onClose}>
              <Text style={L.secondaryText}>Close</Text>
            </TouchableOpacity>
            <TouchableOpacity style={L.primaryBtn} onPress={onRetry}>
              <FeatherIcon name="refresh-cw" size={14} color="#fff" />
              <Text style={L.primaryText}>Retry</Text>
            </TouchableOpacity>
          </RNView>
        )}
      </RNView>
    </RNView>
  );
};

// ─── Main Modal ───────────────────────────────────────────────────────────────

type SheetKind =
  | { kind: "customer" }
  | { kind: "grower" }
  | { kind: "requestType" }
  | { kind: "address" }
  | { kind: "date" }
  | { kind: "pickupDate" }
  | { kind: "uom"; uniqueKey: string }
  | { kind: "stateFilter" }
  | { kind: "locationFilter" }
  | null;

const CreateSalesOrderModal: React.FC<CreateSalesOrderModalProps> = ({
  visible,
  onClose,
  loadCustomers,
  loadGrowers,
  loadProducts,
  onSubmit,
  initialCustomerId,
  editOrder,
  appointmentTimeZone = "Australia/Melbourne",
  businessHours = DEFAULT_BUSINESS,
}) => {
  const C = useTokens();
  const S = createS(C);
  const { width } = useWindowDimensions();
  const wide = width >= WIDE_BREAKPOINT;
  const half = wide ? "48%" : "100%";

  // Data
  const [customers, setCustomers] = useState<SalesCustomer[]>([]);
  const [growers, setGrowers] = useState<Grower[]>([]);
  const [products, setProducts] = useState<ProductData[]>([]);
  /** Every product seen for this customer, so ticked rows survive a filter change */
  const [catalog, setCatalog] = useState<Record<string, ProductData>>({});
  const remember = useCallback(
    (list: ProductData[]) =>
      setCatalog((prev) => {
        const next = { ...prev };
        list.forEach((p) => (next[p.UniqueKey] = p));
        return next;
      }),
    [],
  );

  // Inventory filters live in redux (salesOrders.stateFilter / locationFilter);
  // getSalesCustomerProducts reads them and sends State / Location together
  const {
    states: stateList,
    loadingStates,
    hasFetchedStates,
    stateFilter: stateValue,
    locationFilter: locationValue,
    locations: locationOptions,
  } = useSelector((st: any) => st.portal.admin.salesOrders);
  const stateFilter = stateValue && stateValue !== "all" ? stateValue : "";
  const locationFilter =
    locationValue && locationValue !== "all" ? locationValue : "";
  const states: string[] = useMemo(
    () =>
      [
        ...new Set<string>(
          (stateList ?? [])
            .map((x: any) =>
              String(
                typeof x === "string" ? x : (x?.name ?? x?.code ?? ""),
              ).trim(),
            )
            .filter(Boolean),
        ),
      ].sort((a, b) => a.localeCompare(b)),
    [stateList],
  );
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingCustomerData, setLoadingCustomerData] = useState(false);
  const [customersLoaded, setCustomersLoaded] = useState(false);
  const [dataReadyFor, setDataReadyFor] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState<null | "customers" | "data">(
    null,
  );
  const [customersReloadKey, setCustomersReloadKey] = useState(0);
  const [dataReloadKey, setDataReloadKey] = useState(0);

  // Form
  const [customerId, setCustomerId] = useState("");
  const [growerId, setGrowerId] = useState("");
  const [requestType, setRequestType] =
    useState<RequestType>("Request Delivery");
  const [requiredDate, setRequiredDate] = useState("");
  const [deliveryTo, setDeliveryTo] = useState("");
  const [poNumber, setPoNumber] = useState("");
  const [remarks, setRemarks] = useState("");
  const [rows, setRows] = useState<Record<string, RowState>>({});
  const [search, setSearch] = useState("");

  // Pickup appointment
  const [pickupDate, setPickupDate] = useState("");
  const [pickupSlot, setPickupSlot] = useState("");
  // Bookings cached per `${customer}|${calendar}|YYYY-MM-DD`
  const [dayBookings, setDayBookings] = useState<Record<string, BookedSlot[]>>(
    {},
  );
  const [dayStatus, setDayStatus] = useState<Record<string, LoadStatus>>({});
  const dayStatusRef = useRef<Record<string, LoadStatus>>({});
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [warehousesLoaded, setWarehousesLoaded] = useState(false);
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [sheet, setSheet] = useState<SheetKind>(null);

  // Edit mode
  const isEdit = !!editOrder;
  const lockedCustomerId = editOrder
    ? idOf(editOrder.organization) || null
    : (initialCustomerId ?? null);
  const prefilledRef = useRef<string | null>(null);
  /** The order's current pickup slot, applied once the warehouse time zone is known */
  const [pendingAppt, setPendingAppt] = useState<string | null>(null);
  const myAppts = useMemo(() => ownAppointments(editOrder), [editOrder]);
  const myApptIds = useMemo(
    () => new Set(myAppts.map((a) => a._id).filter(Boolean) as string[]),
    [myAppts],
  );
  const myApptStarts = useMemo(
    () => new Set(myAppts.map((a) => new Date(a.startDateTime!).getTime())),
    [myAppts],
  );
  /** This order's own booking shouldn't block its own slot */
  const withoutOwn = useCallback(
    (list: BookedSlot[]) =>
      myAppts.length === 0
        ? list
        : list.filter((b) =>
            b._id
              ? !myApptIds.has(b._id)
              : !myApptStarts.has(new Date(b.startDateTime).getTime()),
          ),
    [myAppts, myApptIds, myApptStarts],
  );
  const dispatch = useDispatch<any>();

  const customer = customers.find((c) => c._id === customerId) || null;
  const grower = growers.find((g) => g._id === growerId) || null;
  const isPickup = requestType === "Pick up";
  // Pickups always use the grower's default warehouse — there's no manual pick
  const warehouse = grower?.defaultWarehouse
    ? (warehouses.find((w) => w._id === grower.defaultWarehouse) ?? null)
    : null;
  const calendarId = warehouse?.calendarId || "";
  const pickupTimeZone = warehouse?.timezone || appointmentTimeZone;
  const growerPending = isPickup && growers.length > 0 && !grower;
  /** Pickup can't go ahead: grower has no default warehouse, or it isn't in the list */
  const warehouseMissing =
    isPickup &&
    !growerPending &&
    (!grower?.defaultWarehouse || (warehousesLoaded && !warehouse));
  const warehouseMissingMsg = !grower
    ? "This customer has no grower with a default warehouse, so a pickup order can't be created."
    : `Default warehouse is missing for ${grower.name}. Pickup orders need one — set a default warehouse on this grower, or choose a different request type.`;

  const clearError = useCallback((...keys: string[]) => {
    setErrors((prev) => {
      if (!keys.some((k) => k in prev)) return prev;
      const next = { ...prev };
      keys.forEach((k) => delete next[k]);
      return next;
    });
  }, []);

  // ── Reset & load customers when opened ─────────────────────────────────
  useEffect(() => {
    if (!visible) return;
    setCustomerId("");
    setGrowerId("");
    setRequestType("Request Delivery");
    setRequiredDate("");
    setDeliveryTo("");
    setPoNumber("");
    setRemarks("");
    setRows({});
    setSearch("");
    setPickupDate("");
    setPickupSlot("");
    setDayBookings({});
    setDayStatus({});
    dayStatusRef.current = {};
    setErrors({});
    setSubmitError(null);
    setGrowers([]);
    setProducts([]);
    setCatalog({});
    dispatch(resetSalesProductFilters());
    setCustomersLoaded(false);
    setDataReadyFor(null);
    setLoadFailed(null);
    setPendingAppt(null);
    prefilledRef.current = null;

    let cancelled = false;
    setLoadingCustomers(true);
    loadCustomers()
      .then((list) => {
        if (cancelled) return;
        setCustomers(list);
        setCustomersLoaded(true);
        if (lockedCustomerId) {
          if (list.some((c) => c._id === lockedCustomerId)) {
            setCustomerId(lockedCustomerId);
          } else {
            setSubmitError(
              "You aren't assigned as a sales rep for this customer",
            );
          }
        }
      })
      .catch(() => {
        if (cancelled) return;
        setSubmitError("Couldn't load customers");
        setLoadFailed("customers");
      })
      .finally(() => !cancelled && setLoadingCustomers(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, loadCustomers, lockedCustomerId, customersReloadKey]);

  // ── Load growers + products for the selected customer ──────────────────
  useEffect(() => {
    if (!customer) return;
    let cancelled = false;
    setDataReadyFor(null);
    setLoadFailed(null);
    setLoadingCustomerData(true);
    Promise.all([loadGrowers(customer._id), loadProducts(customer)])
      .then(([g, p]) => {
        if (cancelled) return;
        setGrowers(g);
        setProducts(p);
        remember(p);
        setDataReadyFor(customer._id);
      })
      .catch(() => {
        if (cancelled) return;
        setSubmitError("Couldn't load products for this customer");
        setLoadFailed("data");
      })
      .finally(() => !cancelled && setLoadingCustomerData(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?._id, dataReloadKey]);

  // ── States for the filter, from the API (once) ───────────────────────
  useEffect(() => {
    if (visible && !hasFetchedStates && !loadingStates) {
      dispatch(getSalesStates());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, hasFetchedStates]);

  /**
   * Set one filter in redux, then reload — the thunk sends State and
   * Location together. Same pattern as the web products page.
   */
  const applyFilter = async (kind: "state" | "location", value: string) => {
    if (!customer) return;
    const prev = { state: stateFilter, location: locationFilter };
    const next = value || "all";
    dispatch(
      kind === "state"
        ? setSalesStateFilter(next)
        : setSalesLocationFilter(next),
    );
    setLoadingProducts(true);
    try {
      const p = await loadProducts(customer);
      setProducts(p);
      remember(p);
    } catch (err: any) {
      setSubmitError(
        err?.response?.data?.message ??
          err?.message ??
          "Couldn't load products for these filters",
      );
      // Back to the filters the table is showing
      dispatch(setSalesStateFilter(prev.state || "all"));
      dispatch(setSalesLocationFilter(prev.location || "all"));
    } finally {
      setLoadingProducts(false);
    }
  };

  const blocking =
    visible &&
    (loadFailed !== null ||
      loadingCustomers ||
      !customersLoaded ||
      (!!customer && dataReadyFor !== customer._id));

  const dataStep: StepState =
    customer && dataReadyFor === customer._id
      ? "done"
      : loadingCustomerData || loadFailed === "data"
        ? "loading"
        : "pending";

  const loaderSteps: { label: string; state: StepState }[] = [
    {
      label: "Customers",
      state: customersLoaded
        ? "done"
        : loadingCustomers || loadFailed === "customers"
          ? "loading"
          : "pending",
    },
    ...(customer || lockedCustomerId
      ? [
          { label: "Growers & delivery addresses", state: dataStep },
          { label: "Products & pricing", state: dataStep },
        ]
      : []),
  ];

  const retryLoad = () => {
    setSubmitError(null);
    if (loadFailed === "customers") setCustomersReloadKey((k) => k + 1);
    else setDataReloadKey((k) => k + 1);
  };

  // ── Dates ───────────────────────────────────────────────────────────────
  const originalRequired = dayOf(editOrder?.details?.[0]?.requiredDate);
  const requiredMin = useMemo(() => {
    const min = nextWeekday(
      addDays(startOfToday(), customer?.requiredDateLeadDays ?? 4),
    );
    // Editing: the order's current date stays selectable even if it's now
    // inside the lead time
    if (originalRequired) {
      const o = fromIso(originalRequired);
      if (o < min) return o;
    }
    return min;
  }, [customer?.requiredDateLeadDays, originalRequired]);
  // Pickup can't be before the required date
  const pickupMin = requiredDate ? fromIso(requiredDate) : requiredMin;

  // Pickup date defaults to the required date; the rep can move it later
  useEffect(() => {
    if (!isPickup || !requiredDate) return;
    setPickupDate(requiredDate);
    setPickupSlot("");
  }, [isPickup, requiredDate]);

  // ── Edit: prefill once growers + products for the customer are loaded ──
  useEffect(() => {
    if (!editOrder || !customer || dataReadyFor !== customer._id) return;
    if (prefilledRef.current === editOrder._id) return;
    prefilledRef.current = editOrder._id;

    const details = editOrder.details ?? [];
    const first = details[0];
    const rt: RequestType = (REQUEST_TYPES as readonly string[]).includes(
      editOrder.requestType ?? "",
    )
      ? (editOrder.requestType as RequestType)
      : "Request Delivery";

    const gid = idOf(editOrder.subOrganization);
    setGrowerId(growers.some((g) => g._id === gid) ? gid : "");
    setRequestType(rt);
    setPoNumber(editOrder.poNumber ?? "");
    setRemarks(editOrder.remarks ?? "");
    setRequiredDate(dayOf(first?.requiredDate));
    setDeliveryTo(rt === "Pick up" ? "" : (first?.deliveryTo ?? ""));

    // Ticked rows; items no longer in the customer's catalogue are kept
    const known = new Map<string, ProductData>(
      products.map((p) => [p.UniqueKey, p]),
    );
    const extra: ProductData[] = [];
    const nextRows: Record<string, RowState> = {};
    for (const d of details) {
      const key = d.uniqueKey;
      if (!key) continue;
      let p: ProductData | undefined = known.get(key);
      if (!p) {
        p = {
          UniqueKey: key,
          ["Portal Item Name"]: d.itemName ?? key,
          ItemCodes: d.itemCodes ?? [],
          PerCtns: d.perCtns,
          PerPallet: d.perPallet,
          PicturName: d.pictureName ?? null,
          pricelist: null,
        };
        known.set(key, p);
        extra.push(p);
      }
      const uom: Uom = (UOMS as string[]).includes(d.uom ?? "")
        ? (d.uom as Uom)
        : "Pieces";
      const priceText = d.unitPrice != null ? String(d.unitPrice) : "";
      nextRows[key] = {
        selected: true,
        qtyText: d.quantity ? String(d.quantity) : "",
        uom,
        priceText,
        priceEdited: priceText !== listPriceText(p, uom),
      };
    }
    if (extra.length) {
      setProducts((prev) => [...prev, ...extra]);
      remember(extra);
    }
    setRows(nextRows);

    if (rt === "Pick up" && myAppts[0]?.startDateTime) {
      setPendingAppt(myAppts[0].startDateTime);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editOrder, customer?._id, dataReadyFor]);

  // ── Warehouses (loaded for every pickup order) ─────────────────────────
  useEffect(() => {
    if (!isPickup || warehousesLoaded || loadingWarehouses) return;
    setLoadingWarehouses(true);
    dispatch(getWarehouseList())
      .unwrap()
      .then((res: any) => {
        const raw = res?.data?.warehouses ?? res?.data ?? [];
        setWarehouses(
          (Array.isArray(raw) ? raw : []).map((w: any) => ({
            _id: w._id,
            name: w.name,
            calendarId: w.calendarId ?? "",
            timezone: w.timezone ?? null,
          })),
        );
        setWarehousesLoaded(true);
      })
      .catch(() => setWarehouses([]))
      .finally(() => setLoadingWarehouses(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPickup, warehousesLoaded]);

  // ── Edit: restore the current pickup slot in the warehouse's time zone ──
  // Declared after the pickup-date sync so it wins when both run together.
  useEffect(() => {
    if (!pendingAppt || !isPickup) return;
    if (!warehousesLoaded && grower?.defaultWarehouse) return; // tz not known yet
    const { date, time } = wallTimeIn(pendingAppt, pickupTimeZone);
    setPickupDate(date);
    setPickupSlot(time);
    setPendingAppt(null);
  }, [
    pendingAppt,
    isPickup,
    warehousesLoaded,
    grower?.defaultWarehouse,
    pickupTimeZone,
  ]);

  // ── Bookings: fetched one day at a time for the warehouse's calendar ───
  const bookingKey = (iso: string) => `${customer?._id}|${calendarId}|${iso}`;

  const setStatus = (key: string, st: LoadStatus) => {
    dayStatusRef.current = { ...dayStatusRef.current, [key]: st };
    setDayStatus(dayStatusRef.current);
  };

  /** Fetch bookings for one day on the warehouse's calendar */
  const fetchDay = useCallback(
    (iso: string, force = false) => {
      if (!isPickup || !customer?._id || !calendarId || !iso) return;
      const key = `${customer._id}|${calendarId}|${iso}`;
      const current = dayStatusRef.current[key];
      if (current === "loading" || (current === "done" && !force)) return;
      setStatus(key, "loading");
      const { start, end } = dayRange(iso);
      dispatch(
        getAppointmentsByDate({
          startDate: start.toISOString(),
          endDate: end.toISOString(),
          organizationId: customer._id,
          calendarId,
        }),
      )
        .unwrap()
        .then((res: any) => {
          const list: BookedSlot[] = res?.data?.appointments ?? [];
          setDayBookings((prev) => ({ ...prev, [key]: list }));
          setStatus(key, "done");
        })
        .catch(() => setStatus(key, "error"));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, isPickup, customer?._id, calendarId],
  );

  // Runs when the required date sets the pickup date, when a date is picked
  // on the calendar, and when the warehouse/calendar resolves or changes
  useEffect(() => {
    if (pickupDate) fetchDay(pickupDate, true);
  }, [pickupDate, fetchDay]);

  const pickupKey = pickupDate && calendarId ? bookingKey(pickupDate) : "";
  const booked = useMemo(
    () => withoutOwn(dayBookings[pickupKey] ?? NO_BOOKINGS),
    [dayBookings, pickupKey, withoutOwn],
  );
  const loadingSlots = !!pickupKey && dayStatus[pickupKey] === "loading";
  const slotsFailed = !!pickupKey && dayStatus[pickupKey] === "error";

  /** Days already fetched with no free slot show as Full */
  const isDayFull = useCallback(
    (iso: string) => {
      const list = dayBookings[bookingKey(iso)];
      if (!list) return false;
      return buildSlots(iso, businessHours, withoutOwn(list)).every(
        (sl) => sl.disabled,
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dayBookings, businessHours, customer?._id, calendarId, withoutOwn],
  );

  const slots = useMemo(
    () => (pickupDate ? buildSlots(pickupDate, businessHours, booked) : []),
    [pickupDate, businessHours, booked],
  );

  // Drop the chosen slot if it's no longer free
  useEffect(() => {
    if (pickupSlot && slots.find((s) => s.start === pickupSlot)?.disabled) {
      setPickupSlot("");
    }
  }, [slots, pickupSlot]);

  const freeSlots = slots.filter((s) => !s.disabled).length;

  // ── Row helpers ─────────────────────────────────────────────────────────
  const rowOf = (p: ProductData): RowState =>
    rows[p.UniqueKey] ?? defaultRow(p);

  const patchRow = (p: ProductData, patch: Partial<RowState>) =>
    setRows((prev) => ({
      ...prev,
      [p.UniqueKey]: { ...(prev[p.UniqueKey] ?? defaultRow(p)), ...patch },
    }));

  const toggleRow = (p: ProductData) => {
    patchRow(p, { selected: !rowOf(p).selected });
    clearError("items", `${p.UniqueKey}_qty`, `${p.UniqueKey}_price`);
  };

  const setRowQty = (p: ProductData, text: string) => {
    const qtyText = text.replace(/[^0-9]/g, "");
    // Typing a quantity ticks the row
    patchRow(p, {
      qtyText,
      ...(parseQty(qtyText) > 0 && { selected: true }),
    });
    clearError("items", `${p.UniqueKey}_qty`);
  };

  const setRowPrice = (p: ProductData, text: string) => {
    const cleaned = text.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");
    patchRow(p, { priceText: cleaned, priceEdited: true });
    clearError(`${p.UniqueKey}_price`);
  };

  const setRowUom = (p: ProductData, uom: Uom) => {
    const r = rowOf(p);
    patchRow(p, {
      uom,
      ...(!r.priceEdited && { priceText: listPriceText(p, uom) }),
    });
  };

  // ── Handlers ────────────────────────────────────────────────────────────
  const selectCustomer = (id: string) => {
    if (id === customerId) return;
    setCustomerId(id);
    setGrowerId("");
    setDeliveryTo("");
    setRequiredDate("");
    setPickupDate("");
    setPickupSlot("");
    setRows({}); // prices are per customer
    setSearch("");
    setGrowers([]);
    setProducts([]);
    setCatalog({});
    dispatch(resetSalesProductFilters());
    clearError("customer");
  };

  // ── Derived ─────────────────────────────────────────────────────────────
  const visibleProducts = useMemo(() => {
    // State and Location are already applied by the API; search is local
    const q = lc(search);
    if (!q) return products;
    return products.filter(
      (p) =>
        lc(p?.["Portal Item Name"]).includes(q) ||
        lc(p.UniqueKey).includes(q) ||
        listOf(p.Location).some((l) => lc(l).includes(q)) ||
        listOf(p.State).some((st) => lc(st).includes(q)) ||
        ([] as unknown[])
          .concat(p.ItemCodes ?? [])
          .some((c) => lc(c).includes(q)),
    );
  }, [products, search]);

  const addressOptions: SheetOption[] = useMemo(() => {
    const list = grower?.address?.length
      ? grower.address
      : (customer?.contacts || []).map((c) => c.address).filter(Boolean);
    return list.map((a) => ({ value: a, label: a }));
  }, [grower, customer]);

  // From the catalog, so ticked items hidden by a filter stay on the order
  const selectedProducts = (Object.values(catalog) as ProductData[]).filter(
    (p) => rows[p.UniqueKey]?.selected,
  );
  const hiddenSelected = useMemo(() => {
    const shown = new Set(visibleProducts.map((p) => p.UniqueKey));
    return selectedProducts.filter((p) => !shown.has(p.UniqueKey)).length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleProducts, rows, catalog]);

  const totals = useMemo(() => {
    let pieces = 0;
    let pricing = 0;
    for (const p of selectedProducts) {
      const r = rows[p.UniqueKey];
      const qty = parseQty(r.qtyText);
      pieces += toPieces(qty, r.uom, p.PerCtns || 0, p.PerPallet || 0);
      const up = parsePrice(r.priceText);
      if (up != null) pricing += round4(up * qty);
    }
    const subtotal = parseFloat(pricing.toFixed(2));
    const gst = parseFloat((subtotal * GST_RATE).toFixed(2)); // same 10% GST as the server
    return {
      pieces,
      subtotal,
      gst,
      total: parseFloat((subtotal + gst).toFixed(2)),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, catalog]);

  // ── Validation ──────────────────────────────────────────────────────────
  const validate = (): Record<string, string> => {
    const e: Record<string, string> = {};
    if (!customer) e.customer = "Select a customer";
    if (growers.length > 0 && !growerId) e.grower = "Select a grower";
    if (!requiredDate) e.requiredDate = "Select a required date";
    if (!isPickup && !deliveryTo) e.deliveryTo = "Delivery address is required";
    if (isPickup) {
      if (warehouseMissing) e.warehouse = "Default warehouse is missing";
      else if (warehouse && !warehouse.calendarId)
        e.warehouse = "This warehouse has no pickup calendar";
      if (!pickupDate) e.pickupDate = "Select a pickup date";
      else if (!pickupSlot) e.pickupSlot = "Select a pickup time";
    }
    if (selectedProducts.length === 0) e.items = "Tick at least one product";
    for (const p of selectedProducts) {
      const r = rows[p.UniqueKey];
      if (parseQty(r.qtyText) <= 0) e[`${p.UniqueKey}_qty`] = "Qty";
      if (parsePrice(r.priceText) == null) e[`${p.UniqueKey}_price`] = "Price";
    }
    return e;
  };

  // ── Submit ──────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setSubmitError(null);
    if (warehouseMissing) {
      setSubmitError(warehouseMissingMsg);
      setErrors((prev) => ({
        ...prev,
        warehouse: "Default warehouse is missing",
      }));
      return;
    }
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    if (!customer) return;

    const payload: SalesOrderPayload = {
      organization: customer._id,
      subOrganization: growerId || null,
      requestType,
      poNumber: poNumber.trim(),
      remarks: remarks.trim(),
      ...(isPickup && {
        startDateTime: `${pickupDate}T${pickupSlot}:00`,
        endDateTime: `${pickupDate}T${addMinutes(pickupSlot, SLOT_MINUTES)}:00`,
        timezone: pickupTimeZone,
      }),
      details: selectedProducts.map((p) => {
        const r = rows[p.UniqueKey];
        const quantity = parseQty(r.qtyText);
        const unitPrice = parsePrice(r.priceText) ?? 0;
        return {
          uniqueKey: p.UniqueKey,
          itemCodes: p.ItemCodes || [],
          itemName: p?.["Portal Item Name"],
          pictureName: p.PicturName || null,
          quantity,
          uom: r.uom,
          perCtns: p.PerCtns || 0,
          perPallet: p.PerPallet || 0,
          requiredDate,
          ...(!isPickup && { deliveryTo }),
          ...(isPickup && {
            warehouse: warehouse?._id ?? null,
            timezone: pickupTimeZone,
          }),
          unitPrice,
          totalPrice: round4(unitPrice * quantity),
        };
      }),
    };

    setSubmitting(true);
    try {
      await onSubmit(payload);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ?? err?.message ?? "Order creation failed";
      setSubmitError(Array.isArray(msg) ? msg.join("\n") : String(msg));
    } finally {
      setSubmitting(false);
    }
  };

  // ── Sheet config ────────────────────────────────────────────────────────
  const optionSheet = (() => {
    if (!sheet) return null;
    switch (sheet.kind) {
      case "customer":
        return {
          title: "Customer",
          searchable: true,
          options: customers.map((c) => ({
            value: c._id,
            label: str(c.name),
            sub: ([] as unknown[])
              .concat(c.cardCode ?? [])
              .map(str)
              .join(" · "),
          })),
          selected: customerId,
          emptyText: "You aren't assigned to any customers",
          onSelect: selectCustomer,
        };
      case "grower":
        return {
          title: "Grower",
          searchable: growers.length > 8,
          options: growers.map((g) => ({ value: g._id, label: g.name })),
          selected: growerId,
          onSelect: (id: string) => {
            if (id !== growerId) setPickupSlot("");
            setGrowerId(id);
            setDeliveryTo("");
            clearError("grower", "warehouse");
          },
        };
      case "requestType":
        return {
          title: "Request type",
          options: REQUEST_TYPES.map((t) => ({ value: t, label: t })),
          selected: requestType,
          onSelect: (v: string) => {
            setRequestType(v as RequestType);
            if (v === "Pick up") {
              setDeliveryTo("");
              clearError("deliveryTo");
            } else {
              setPickupDate("");
              setPickupSlot("");
              clearError("pickupDate", "pickupSlot", "warehouse");
            }
          },
        };
      case "address":
        return {
          title: "Delivery to",
          options: addressOptions,
          selected: deliveryTo,
          emptyText: "No addresses on file for this customer",
          onSelect: (v: string) => {
            setDeliveryTo(v);
            clearError("deliveryTo");
          },
        };
      case "stateFilter":
        return {
          title: "State",
          searchable: states.length > 8,
          options: [
            { value: "", label: "All states" },
            ...states.map((st) => ({ value: st, label: st })),
          ],
          selected: stateFilter,
          emptyText: loadingStates ? "Loading states…" : "No states",
          onSelect: (v: string) => applyFilter("state", v),
        };
      case "locationFilter":
        return {
          title: "Location",
          searchable: locationOptions.length > 8,
          options: [
            { value: "", label: "All locations" },
            ...(
              (locationFilter && !locationOptions.includes(locationFilter)
                ? [locationFilter, ...locationOptions]
                : locationOptions) as string[]
            ).map((l) => ({ value: l, label: l })),
          ],
          selected: locationFilter,
          onSelect: (v: string) => applyFilter("location", v),
        };
      case "uom": {
        const p = catalog[sheet.uniqueKey] as ProductData | undefined;
        return {
          title: "Unit of measure",
          options: UOMS.map((u) => ({ value: u, label: u })),
          selected: p ? rowOf(p).uom : undefined,
          onSelect: (v: string) => p && setRowUom(p, v as Uom),
        };
      }
      default:
        return null;
    }
  })();

  const hasErrors = Object.keys(errors).length > 0;
  const hasNewPrices = selectedProducts.some((p) => {
    const r = rows[p.UniqueKey];
    const up = parsePrice(r.priceText);
    const list = priceEntryFor(p.pricelist, r.uom)?.price;
    return up != null && (list == null || round4(list) !== up);
  });

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <Modal visible={visible} animationType="slide" onRequestClose={onClose}>
      <RNView style={S.screen}>
        <KeyboardAvoidingView
          behavior={Platform.OS === "ios" ? "padding" : undefined}
          style={{ flex: 1 }}
        >
          {/* ── Header ── */}
          <RNView style={[S.header, !wide && S.headerMobile]}>
            <RNView style={S.headerIcon}>
              <FeatherIcon name="shopping-cart" size={16} color={C.accent} />
            </RNView>
            <RNView style={{ flex: 1, minWidth: 0 }}>
              <Text style={S.headerTitle}>
                {isEdit
                  ? `Edit order${editOrder?.orderNo ? ` ${editOrder.orderNo}` : ""}`
                  : "New sales order"}
              </Text>
              <Text style={S.headerSub} numberOfLines={1}>
                {customer ? customer.name : "Choose a customer to start"}
              </Text>
            </RNView>
            <Pressable
              onPress={onClose}
              hitSlop={8}
              accessibilityLabel="Close"
              style={(s: any) => [
                S.closeBtn,
                (s.hovered || s.pressed) && { backgroundColor: C.surfaceAlt },
              ]}
            >
              <FeatherIcon name="x" size={18} color={C.textMuted} />
            </Pressable>
          </RNView>

          {/* ── Body ── */}
          <RNView style={S.body}>
            <ScrollView
              style={{ flex: 1 }}
              contentContainerStyle={[
                S.scrollContent,
                !wide && { padding: 14 },
              ]}
              keyboardShouldPersistTaps="handled"
            >
              {!!submitError && !loadFailed && (
                <RNView style={S.errorBanner}>
                  <FeatherIcon name="alert-circle" size={15} color={C.danger} />
                  <Text style={S.errorBannerText}>{submitError}</Text>
                </RNView>
              )}

              {/* ── Customer Info ── */}
              <Text style={S.sectionTitle}>Customer info</Text>
              <RNView style={S.grid}>
                <Field
                  label="Customer"
                  width={half}
                  error={errors.customer}
                  C={C}
                >
                  <SelectBox
                    value={customer?.name}
                    placeholder="Select a customer"
                    onPress={() => setSheet({ kind: "customer" })}
                    locked={!!lockedCustomerId && !!customer}
                    error={!!errors.customer}
                    C={C}
                  />
                </Field>

                <Field
                  label="PO number / customer reference (optional)"
                  width={half}
                  C={C}
                >
                  <TextInput
                    style={[S.box, webNoOutline]}
                    value={poNumber}
                    onChangeText={setPoNumber}
                    placeholderTextColor={C.textMuted}
                  />
                </Field>

                {growers.length > 0 && (
                  <Field
                    label="Grower"
                    width={half}
                    error={errors.grower}
                    C={C}
                  >
                    <SelectBox
                      value={grower?.name}
                      placeholder="Select a grower"
                      onPress={() => setSheet({ kind: "grower" })}
                      error={!!errors.grower}
                      C={C}
                    />
                  </Field>
                )}

                <Field label="Request type" width={half} C={C}>
                  <SelectBox
                    value={requestType}
                    onPress={() => setSheet({ kind: "requestType" })}
                    C={C}
                  />
                </Field>

                {!isPickup && (
                  <Field
                    label="Delivery to"
                    width={half}
                    error={errors.deliveryTo}
                    C={C}
                  >
                    <SelectBox
                      value={deliveryTo}
                      placeholder={
                        customer
                          ? "Select an address"
                          : "Select a customer first"
                      }
                      onPress={() => setSheet({ kind: "address" })}
                      disabled={!customer}
                      error={!!errors.deliveryTo}
                      C={C}
                    />
                  </Field>
                )}

                <Field
                  label="Required date"
                  width={half}
                  error={errors.requiredDate}
                  hint={
                    customer
                      ? `Earliest ${longDate(toIso(requiredMin))} · weekdays only`
                      : undefined
                  }
                  C={C}
                >
                  <SelectBox
                    value={longDate(requiredDate)}
                    placeholder="Pick a date"
                    icon="calendar"
                    onPress={() => setSheet({ kind: "date" })}
                    disabled={!customer}
                    error={!!errors.requiredDate}
                    C={C}
                  />
                </Field>

                <Field label="Remarks (optional)" width="100%" C={C}>
                  <TextInput
                    style={[
                      S.box,
                      { minHeight: 60, textAlignVertical: "top" },
                      webNoOutline,
                    ]}
                    value={remarks}
                    onChangeText={setRemarks}
                    multiline
                  />
                </Field>
              </RNView>

              {/* ── Pickup appointment ── */}
              {isPickup && (
                <RNView
                  style={[
                    S.pickupCard,
                    (warehouseMissing ||
                      !!errors.warehouse ||
                      !!errors.pickupDate ||
                      !!errors.pickupSlot) && {
                      borderColor: C.danger,
                    },
                  ]}
                >
                  <RNView style={S.pickupHead}>
                    <RNView style={S.pickupIcon}>
                      <FeatherIcon name="clock" size={15} color={C.accent} />
                    </RNView>
                    <RNView style={{ flex: 1 }}>
                      <Text style={S.pickupTitle}>Pickup appointment</Text>
                      <Text style={S.pickupSub}>
                        30-minute slot,{" "}
                        {timeLabel(`${pad(businessHours.startHour)}:00`)} –{" "}
                        {timeLabel(`${pad(businessHours.endHour)}:00`)} (
                        {pickupTimeZone})
                      </Text>
                    </RNView>
                  </RNView>

                  {warehouseMissing && (
                    <RNView style={[S.errorBanner, { marginBottom: 14 }]}>
                      <FeatherIcon
                        name="alert-triangle"
                        size={15}
                        color={C.danger}
                      />
                      <Text style={S.errorBannerText}>
                        {warehouseMissingMsg}
                      </Text>
                    </RNView>
                  )}

                  <RNView
                    style={[
                      S.pickupBody,
                      wide && { flexDirection: "row", gap: 20 },
                    ]}
                  >
                    <RNView style={wide ? { width: 260 } : undefined}>
                      <Field
                        label="Warehouse"
                        width="100%"
                        error={errors.warehouse}
                        hint={
                          warehouse ? "Grower's default warehouse" : undefined
                        }
                        C={C}
                      >
                        <SelectBox
                          value={warehouse?.name}
                          placeholder={
                            growerPending
                              ? "Select a grower first"
                              : loadingWarehouses
                                ? "Loading…"
                                : warehouseMissing
                                  ? "No default warehouse"
                                  : "—"
                          }
                          onPress={() => {}}
                          locked
                          error={!!errors.warehouse || warehouseMissing}
                          C={C}
                        />
                      </Field>
                      <Field
                        label="Pickup date"
                        width="100%"
                        error={errors.pickupDate}
                        C={C}
                      >
                        <SelectBox
                          value={longDate(pickupDate)}
                          placeholder={
                            requiredDate
                              ? "Pick a date"
                              : "Set the required date first"
                          }
                          icon="calendar"
                          onPress={() => setSheet({ kind: "pickupDate" })}
                          disabled={!requiredDate}
                          error={!!errors.pickupDate}
                          C={C}
                        />
                      </Field>
                      {!!pickupDate && !!pickupSlot && (
                        <RNView style={S.slotSummary}>
                          <FeatherIcon
                            name="check-circle"
                            size={14}
                            color={C.success}
                          />
                          <Text style={S.slotSummaryText}>
                            {longDate(pickupDate)}, {timeLabel(pickupSlot)} –{" "}
                            {timeLabel(addMinutes(pickupSlot, SLOT_MINUTES))}
                          </Text>
                        </RNView>
                      )}
                    </RNView>

                    <RNView style={{ flex: 1 }}>
                      <RNView style={S.slotLabelRow}>
                        <Text style={S.fieldLabel}>Pickup time</Text>
                        {loadingSlots && (
                          <ActivityIndicator size="small" color={C.accent} />
                        )}
                        {!!pickupKey && !loadingSlots && (
                          <Text style={S.slotCount}>
                            {freeSlots} of {slots.length} free
                          </Text>
                        )}
                      </RNView>
                      {growerPending ? (
                        <Text style={S.slotEmpty}>
                          Choose a grower to see times.
                        </Text>
                      ) : !calendarId ? (
                        <Text style={S.slotEmpty}>
                          {warehouseMissing
                            ? "Pickup times aren't available without a default warehouse."
                            : loadingWarehouses || !warehouse
                              ? "Loading warehouses…"
                              : "This warehouse has no pickup calendar."}
                        </Text>
                      ) : !pickupDate ? (
                        <Text style={S.slotEmpty}>
                          Choose a pickup date to see times.
                        </Text>
                      ) : (
                        <RNView style={S.slotGrid}>
                          {slots.map((s) => {
                            const on = s.start === pickupSlot;
                            return (
                              <Pressable
                                key={s.start}
                                onPress={() => {
                                  setPickupSlot(s.start);
                                  clearError("pickupSlot");
                                }}
                                disabled={s.disabled || loadingSlots}
                                accessibilityLabel={`${s.label}${s.disabled ? ", unavailable" : ""}`}
                                accessibilityState={{
                                  disabled: s.disabled,
                                  selected: on,
                                }}
                                style={(st: any) => [
                                  S.slot,
                                  st.hovered &&
                                    !s.disabled &&
                                    !on &&
                                    S.slotHover,
                                  s.disabled && S.slotDisabled,
                                  on && S.slotOn,
                                ]}
                              >
                                <Text
                                  style={[
                                    S.slotText,
                                    s.disabled && S.slotTextDisabled,
                                    on && { color: "#fff" },
                                  ]}
                                >
                                  {s.label}
                                </Text>
                              </Pressable>
                            );
                          })}
                        </RNView>
                      )}
                      {!!errors.pickupSlot && (
                        <RNView style={[S.errorRow, { marginTop: 8 }]}>
                          <FeatherIcon
                            name="alert-circle"
                            size={11}
                            color={C.danger}
                          />
                          <Text style={S.errorText}>{errors.pickupSlot}</Text>
                        </RNView>
                      )}
                      {slotsFailed && (
                        <Text style={[S.hintText, { color: C.warning }]}>
                          Couldn't check existing bookings — times shown may
                          already be taken.
                        </Text>
                      )}
                      {!!pickupKey && freeSlots === 0 && !loadingSlots && (
                        <Text style={[S.hintText, { marginTop: 8 }]}>
                          No free times on this day — try another date.
                        </Text>
                      )}
                    </RNView>
                  </RNView>
                </RNView>
              )}

              {/* ── Inventory ── */}
              <RNView
                style={[S.inventoryHeader, !wide && { flexWrap: "wrap" }]}
              >
                <Text style={[S.sectionTitle, { marginBottom: 0 }]}>
                  Inventory
                </Text>
                {loadingProducts && (
                  <ActivityIndicator size="small" color={C.accent} />
                )}
                <RNView style={{ flex: 1 }} />
                <Text style={S.productCount}>
                  {visibleProducts.length === products.length
                    ? `${products.length} product${products.length === 1 ? "" : "s"}`
                    : `${visibleProducts.length} of ${products.length}`}
                </Text>
                {(states.length > 0 || loadingStates) && (
                  <FilterChip
                    label="State"
                    value={stateFilter || "All"}
                    active={!!stateFilter}
                    busy={loadingStates}
                    disabled={!customer || loadingProducts}
                    onPress={() => setSheet({ kind: "stateFilter" })}
                    C={C}
                  />
                )}
                <FilterChip
                  label="Location"
                  value={locationFilter || "All"}
                  active={!!locationFilter}
                  disabled={
                    !customer ||
                    loadingProducts ||
                    (locationOptions.length === 0 && !locationFilter)
                  }
                  onPress={() => setSheet({ kind: "locationFilter" })}
                  C={C}
                />
                <Pressable
                  style={(s: any) => [
                    S.refreshBtn,
                    (s.hovered || s.pressed) && { opacity: 0.8 },
                  ]}
                  onPress={() => setDataReloadKey((k) => k + 1)}
                  disabled={!customer}
                  accessibilityLabel="Reload products"
                >
                  <FeatherIcon name="refresh-cw" size={14} color={C.textMid} />
                </Pressable>
                <RNView
                  style={[
                    S.searchWrap,
                    !wide && { width: "100%", marginTop: 10 },
                  ]}
                >
                  <FeatherIcon name="search" size={13} color={C.textMuted} />
                  <TextInput
                    style={[S.searchInput, webNoOutline]}
                    value={search}
                    onChangeText={setSearch}
                    placeholder="Search products…"
                    placeholderTextColor={C.textMuted}
                    autoCorrect={false}
                  />
                </RNView>
              </RNView>

              {/* Table + loader while State / Location filters reload it */}
              <RNView
                style={[S.tableWrap, loadingProducts && S.tableWrapLoading]}
              >
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator
                  contentContainerStyle={{ flexGrow: 1 }}
                  scrollEnabled={!loadingProducts}
                >
                  <RNView
                    style={[
                      S.table,
                      { minWidth: TABLE_WIDTH },
                      !!errors.items && { borderColor: C.danger },
                    ]}
                  >
                    {/* Header */}
                    <RNView style={[S.tr, S.thRow]}>
                      <RNView style={{ width: COL.check }} />
                      <Text style={[S.th, S.grow, { width: COL.state }]}>
                        State
                      </Text>
                      <Text style={[S.th, S.grow2, { width: COL.location }]}>
                        Location
                      </Text>
                      <Text style={[S.th, { width: COL.code }]}>Item code</Text>
                      <Text style={[S.th, S.grow, { width: COL.name }]}>
                        Item name
                      </Text>
                      <Text style={[S.th, S.right, { width: COL.available }]}>
                        Available (pcs)
                      </Text>
                      <Text style={[S.th, S.center, { width: COL.qty }]}>
                        Order qty
                      </Text>
                      <Text style={[S.th, S.center, { width: COL.uom }]}>
                        UoM
                      </Text>
                      <Text style={[S.th, S.center, { width: COL.price }]}>
                        Price per UoM
                      </Text>
                      <Text style={[S.th, S.right, { width: COL.total }]}>
                        Total
                      </Text>
                    </RNView>

                    {!customer ? (
                      <Text style={S.emptyTable}>
                        Select a customer to see their products
                      </Text>
                    ) : visibleProducts.length === 0 ? (
                      <Text style={S.emptyTable}>
                        {products.length
                          ? "No products match your search"
                          : "No products assigned to this customer"}
                      </Text>
                    ) : (
                      visibleProducts.map((p, i) => {
                        const r = rowOf(p);
                        const qty = parseQty(r.qtyText);
                        const unit = parsePrice(r.priceText);
                        const list = priceEntryFor(p.pricelist, r.uom)?.price;
                        const isNewPrice =
                          unit != null &&
                          (list == null || round4(list) !== unit);
                        const total =
                          r.selected && qty > 0 && unit != null
                            ? round4(unit * qty)
                            : null;
                        const qtyErr = !!errors[`${p.UniqueKey}_qty`];
                        const priceErr = !!errors[`${p.UniqueKey}_price`];
                        return (
                          <RNView
                            key={p.UniqueKey}
                            style={[
                              S.tr,
                              S.bodyRow,
                              i % 2 === 0 && { backgroundColor: C.rowAlt },
                              r.selected && {
                                backgroundColor: C.accent + "0D",
                              },
                            ]}
                          >
                            <Pressable
                              style={[S.checkCell, { width: COL.check }]}
                              onPress={() => toggleRow(p)}
                              hitSlop={6}
                              accessibilityRole="checkbox"
                              accessibilityState={{ checked: r.selected }}
                            >
                              <RNView
                                style={[S.checkbox, r.selected && S.checkboxOn]}
                              >
                                {r.selected && (
                                  <FeatherIcon
                                    name="check"
                                    size={12}
                                    color="#fff"
                                  />
                                )}
                              </RNView>
                            </Pressable>
                            {/* Every state and location, wrapping onto more lines */}
                            <ChipList
                              values={listOf(p.State)}
                              style={[S.grow, { width: COL.state }]}
                              S={S}
                            />
                            <ChipList
                              values={listOf(p.Location)}
                              style={[S.grow2, { width: COL.location }]}
                              S={S}
                            />
                            <Text style={[S.td, { width: COL.code }]}>
                              {str(p.ItemCodes) || str(p.UniqueKey)}
                            </Text>
                            <Text style={[S.td, S.grow, { width: COL.name }]}>
                              {p?.["Portal Item Name"]}
                            </Text>
                            <Text
                              style={[S.td, S.right, { width: COL.available }]}
                            >
                              {p.Available2 != null && str(p.Available2) !== ""
                                ? Number.isFinite(Number(p.Available2))
                                  ? Number(p.Available2).toLocaleString()
                                  : str(p.Available2)
                                : "-"}
                            </Text>

                            {/* Order Qty */}
                            <RNView style={[S.cellWrap, { width: COL.qty }]}>
                              <TextInput
                                style={[
                                  S.cellInput,
                                  qtyErr && S.cellError,
                                  webNoOutline,
                                ]}
                                value={r.qtyText}
                                onChangeText={(t) => setRowQty(p, t)}
                                placeholder="-"
                                placeholderTextColor={C.textMid}
                                keyboardType="number-pad"
                                selectTextOnFocus
                              />
                            </RNView>

                            {/* UoM */}
                            <RNView style={[S.cellWrap, { width: COL.uom }]}>
                              <Pressable
                                style={(s: any) => [
                                  S.uomBtn,
                                  s.hovered && { borderColor: C.accent + "88" },
                                ]}
                                onPress={() =>
                                  setSheet({
                                    kind: "uom",
                                    uniqueKey: p.UniqueKey,
                                  })
                                }
                              >
                                <Text style={S.uomText}>{r.uom}</Text>
                                <FeatherIcon
                                  name="chevron-down"
                                  size={14}
                                  color={C.text}
                                />
                              </Pressable>
                            </RNView>

                            {/* Price – always editable */}
                            <RNView style={[S.cellWrap, { width: COL.price }]}>
                              <TextInput
                                style={[
                                  S.cellInput,
                                  S.priceInput,
                                  isNewPrice && r.selected && S.cellNew,
                                  priceErr && S.cellError,
                                  webNoOutline,
                                ]}
                                value={r.priceText}
                                onChangeText={(t) => setRowPrice(p, t)}
                                placeholder="0.00"
                                placeholderTextColor={C.textMuted}
                                keyboardType="decimal-pad"
                                selectTextOnFocus
                              />
                            </RNView>

                            <Text style={[S.td, S.right, { width: COL.total }]}>
                              {total != null ? money(total) : "-"}
                            </Text>
                          </RNView>
                        );
                      })
                    )}
                  </RNView>
                </ScrollView>

                {loadingProducts && (
                  <RNView
                    style={S.tableLoader}
                    accessibilityRole="progressbar"
                    accessibilityLabel="Loading products"
                  >
                    <RNView style={S.tableLoaderCard}>
                      <ActivityIndicator size="small" color={C.accent} />
                      <RNView style={{ flexShrink: 1 }}>
                        <Text style={S.tableLoaderTitle}>
                          Loading products…
                        </Text>
                        <Text style={S.tableLoaderSub} numberOfLines={1}>
                          {[stateFilter, locationFilter]
                            .filter(Boolean)
                            .join(" · ") || "All states and locations"}
                        </Text>
                      </RNView>
                    </RNView>
                  </RNView>
                )}
              </RNView>

              {hiddenSelected > 0 && (
                <Text style={[S.hintText, { marginTop: 8 }]}>
                  {hiddenSelected} ticked item
                  {hiddenSelected === 1 ? " is" : "s are"} hidden by the filters
                  and still on the order.
                </Text>
              )}
              {!!errors.items && (
                <RNView style={[S.errorRow, { marginTop: 8 }]}>
                  <FeatherIcon name="alert-circle" size={11} color={C.danger} />
                  <Text style={S.errorText}>{errors.items}</Text>
                </RNView>
              )}
              {hasNewPrices && (
                <RNView style={S.newPriceNote}>
                  <RNView style={S.newPriceSwatch} />
                  <Text style={S.newPriceNoteText}>
                    Highlighted prices will be saved to this customer's price
                    list when you submit.
                  </Text>
                </RNView>
              )}

              {/* Totals */}
              <RNView style={[S.totals, !wide && { alignSelf: "stretch" }]}>
                <RNView style={S.totalRow}>
                  <Text style={S.totalLabel}>Total qty</Text>
                  <Text style={S.totalValue}>
                    {totals.pieces.toLocaleString()} pieces
                  </Text>
                </RNView>
                <RNView style={S.totalRow}>
                  <Text style={S.totalLabel}>Subtotal</Text>
                  <Text style={S.totalValue}>{money(totals.subtotal)}</Text>
                </RNView>
                <RNView style={S.totalRow}>
                  <Text style={S.totalLabel}>GST (10%)</Text>
                  <Text style={S.totalValue}>{money(totals.gst)}</Text>
                </RNView>
                <RNView style={[S.totalRow, S.grandRow]}>
                  <Text style={[S.totalLabel, { color: C.text }]}>Total</Text>
                  <Text style={[S.totalValue, S.grandValue]}>
                    {money(totals.total)}
                  </Text>
                </RNView>
              </RNView>
            </ScrollView>

            {blocking && (
              <LoadingOverlay
                steps={loaderSteps}
                failed={loadFailed !== null}
                message={submitError}
                onRetry={retryLoad}
                onClose={onClose}
                C={C}
              />
            )}
          </RNView>

          {/* ── Footer ── */}
          <RNView style={[S.footer, !wide && S.footerMobile]}>
            <RNView style={{ flex: 1, minWidth: 0 }}>
              {warehouseMissing ? (
                <RNView style={S.errorRow}>
                  <FeatherIcon name="alert-circle" size={13} color={C.danger} />
                  <Text
                    style={[S.errorText, { fontSize: 13 }]}
                    numberOfLines={1}
                  >
                    Default warehouse is missing for pickup
                  </Text>
                </RNView>
              ) : hasErrors ? (
                <RNView style={S.errorRow}>
                  <FeatherIcon name="alert-circle" size={13} color={C.danger} />
                  <Text
                    style={[S.errorText, { fontSize: 13 }]}
                    numberOfLines={1}
                  >
                    Fix the highlighted fields
                  </Text>
                </RNView>
              ) : (
                <Text style={S.footerSummary} numberOfLines={1}>
                  {selectedProducts.length} item
                  {selectedProducts.length === 1 ? "" : "s"} ·{" "}
                  <Text style={{ fontWeight: "800", color: C.text }}>
                    {money(totals.total)}
                  </Text>{" "}
                  incl. GST
                </Text>
              )}
            </RNView>
            {wide && (
              <Pressable
                onPress={onClose}
                disabled={submitting}
                style={(s: any) => [
                  S.cancelBtn,
                  (s.hovered || s.pressed) && { backgroundColor: C.surfaceAlt },
                ]}
              >
                <Text style={S.cancelText}>Cancel</Text>
              </Pressable>
            )}
            <Pressable
              style={(s: any) => [
                S.submitBtn,
                (s.hovered || s.pressed) && { opacity: 0.9 },
                (submitting || blocking || warehouseMissing) && {
                  opacity: 0.6,
                },
              ]}
              onPress={handleSubmit}
              disabled={submitting || blocking || warehouseMissing}
              accessibilityRole="button"
            >
              {submitting ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                <Text style={S.submitText}>
                  {isEdit ? "Save changes" : "Submit order"}
                </Text>
              )}
            </Pressable>
          </RNView>
        </KeyboardAvoidingView>
      </RNView>

      {optionSheet && (
        <OptionSheet
          title={optionSheet.title}
          options={optionSheet.options}
          selected={optionSheet.selected}
          searchable={
            "searchable" in optionSheet ? optionSheet.searchable : false
          }
          emptyText={
            "emptyText" in optionSheet ? optionSheet.emptyText : undefined
          }
          onSelect={optionSheet.onSelect}
          onClose={() => setSheet(null)}
          C={C}
        />
      )}

      {sheet?.kind === "date" && (
        <CalendarSheet
          title="Required date"
          value={requiredDate}
          minDate={requiredMin}
          onSelect={(iso) => {
            setRequiredDate(iso);
            clearError("requiredDate");
          }}
          onClose={() => setSheet(null)}
          C={C}
        />
      )}

      {sheet?.kind === "pickupDate" && (
        <CalendarSheet
          title="Pickup date"
          value={pickupDate}
          minDate={pickupMin}
          note={`Weekdays only, on or after the required date (${longDate(toIso(pickupMin))})`}
          isDayFull={calendarId ? isDayFull : undefined}
          onSelect={(iso) => {
            if (iso !== pickupDate) setPickupSlot("");
            setPickupDate(iso); // triggers the appointment fetch for that day
            clearError("pickupDate");
          }}
          onClose={() => setSheet(null)}
          C={C}
        />
      )}
    </Modal>
  );
};

export default CreateSalesOrderModal;

// ─── Styles ───────────────────────────────────────────────────────────────────

const createS = (C: Tokens) =>
  StyleSheet.create({
    // Shell
    screen: { flex: 1, backgroundColor: C.surface },

    header: {
      flexDirection: "row",
      alignItems: "center",
      gap: 12,
      paddingHorizontal: 24,
      paddingVertical: 14,
      borderBottomWidth: 1,
      borderBottomColor: C.border,
      backgroundColor: C.surface,
      paddingTop: Platform.OS === "ios" ? 52 : 14,
    },
    headerMobile: { paddingHorizontal: 14 },
    headerIcon: {
      width: 36,
      height: 36,
      borderRadius: 10,
      backgroundColor: C.accent + "14",
      justifyContent: "center",
      alignItems: "center",
    },
    headerTitle: { fontSize: 17, fontWeight: "800", color: C.text },
    headerSub: { fontSize: 13, color: C.textMuted, marginTop: 1 },
    closeBtn: {
      width: 36,
      height: 36,
      borderRadius: 8,
      justifyContent: "center",
      alignItems: "center",
    },

    body: { flex: 1, minHeight: 0, backgroundColor: C.surface },
    scrollContent: { padding: 24, paddingBottom: 32 },

    footer: {
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
      paddingHorizontal: 24,
      paddingVertical: 14,
      borderTopWidth: 1,
      borderTopColor: C.border,
      backgroundColor: C.surface,
      paddingBottom: Platform.OS === "ios" ? 28 : 14,
    },
    footerMobile: { paddingHorizontal: 14 },
    footerSummary: { fontSize: 13, color: C.textMid },
    cancelBtn: {
      height: 42,
      paddingHorizontal: 18,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: C.border,
      justifyContent: "center",
      alignItems: "center",
    },
    cancelText: { fontSize: 14, fontWeight: "700", color: C.textMid },
    submitBtn: {
      minWidth: 160,
      height: 42,
      paddingHorizontal: 20,
      borderRadius: 10,
      backgroundColor: C.accent,
      justifyContent: "center",
      alignItems: "center",
    },
    submitText: { fontSize: 15, fontWeight: "800", color: "#fff" },

    errorBanner: {
      flexDirection: "row",
      alignItems: "flex-start",
      gap: 10,
      backgroundColor: C.dangerBg,
      padding: 12,
      borderRadius: 10,
      marginBottom: 16,
    },
    errorBannerText: {
      flex: 1,
      fontSize: 13,
      color: C.danger,
      fontWeight: "600",
    },

    sectionTitle: {
      fontSize: 17,
      fontWeight: "800",
      color: C.text,
      marginBottom: 16,
    },

    // Customer Info grid
    grid: {
      flexDirection: "row",
      flexWrap: "wrap",
      justifyContent: "space-between",
    },
    fieldLabel: {
      fontSize: 12,
      fontWeight: "700",
      color: C.textMid,
      marginBottom: 6,
    },
    hintText: { fontSize: 12, color: C.textMuted, marginTop: 5 },
    box: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
      minHeight: 40,
      paddingHorizontal: 12,
      paddingVertical: 8,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: C.border,
      backgroundColor: C.field,
      fontSize: 14,
      color: C.text,
    },
    boxText: { flex: 1, fontSize: 14, color: C.text },
    errorRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: 4,
      marginTop: 5,
    },
    errorText: { fontSize: 11, color: C.danger, fontWeight: "600" },

    // Pickup appointment
    pickupCard: {
      borderWidth: 1,
      borderColor: C.border,
      borderRadius: 12,
      padding: 16,
      marginBottom: 8,
      backgroundColor: C.accent + "06",
    },
    pickupHead: {
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
      marginBottom: 14,
    },
    pickupIcon: {
      width: 30,
      height: 30,
      borderRadius: 9,
      backgroundColor: C.accent + "14",
      justifyContent: "center",
      alignItems: "center",
    },
    pickupTitle: { fontSize: 15, fontWeight: "800", color: C.text },
    pickupSub: { fontSize: 12, color: C.textMuted, marginTop: 1 },
    pickupBody: {},
    slotLabelRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
      marginBottom: 6,
    },
    slotCount: { fontSize: 12, color: C.textMuted, marginBottom: 6 },
    slotEmpty: {
      fontSize: 13,
      color: C.textMuted,
      paddingVertical: 12,
    },
    slotGrid: { flexDirection: "row", flexWrap: "wrap", gap: 8 },
    slot: {
      minWidth: 92,
      height: 36,
      paddingHorizontal: 10,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: C.border,
      backgroundColor: C.surface,
      justifyContent: "center",
      alignItems: "center",
    },
    slotHover: { borderColor: C.accent + "88" },
    slotDisabled: { backgroundColor: C.surfaceAlt, borderColor: C.surfaceAlt },
    slotOn: { backgroundColor: C.accent, borderColor: C.accent },
    slotText: { fontSize: 13, fontWeight: "700", color: C.text },
    slotTextDisabled: {
      color: C.textMuted,
      textDecorationLine: "line-through",
      fontWeight: "500",
    },
    slotSummary: {
      flexDirection: "row",
      alignItems: "center",
      gap: 6,
      marginTop: -6,
      marginBottom: 12,
    },
    slotSummaryText: {
      fontSize: 13,
      fontWeight: "700",
      color: C.text,
      flex: 1,
    },

    // Inventory header
    inventoryHeader: {
      flexDirection: "row",
      alignItems: "center",
      gap: 12,
      marginTop: 16,
      marginBottom: 14,
    },
    productCount: { fontSize: 13, fontWeight: "700", color: C.textMid },
    filterChip: {
      flexDirection: "row",
      alignItems: "center",
      gap: 6,
      height: 38,
      maxWidth: 220,
      paddingHorizontal: 12,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: C.border,
      backgroundColor: C.surface,
    },
    filterChipActive: {
      borderColor: C.accent + "66",
      backgroundColor: C.accent + "0D",
    },
    filterChipLabel: { fontSize: 12, fontWeight: "600", color: C.textMuted },
    filterChipValue: {
      fontSize: 13,
      fontWeight: "700",
      color: C.text,
      flexShrink: 1,
    },
    refreshBtn: {
      width: 38,
      height: 38,
      borderRadius: 8,
      backgroundColor: C.field,
      justifyContent: "center",
      alignItems: "center",
    },
    searchWrap: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
      width: 300,
      height: 38,
      paddingHorizontal: 12,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: C.border,
      backgroundColor: C.surface,
    },
    searchInput: { flex: 1, fontSize: 13, color: C.text, paddingVertical: 0 },

    // Table
    tableWrap: { position: "relative" },
    // Room for the loader card when the table is short or empty
    tableWrapLoading: { minHeight: 160 },
    tableLoader: {
      ...StyleSheet.absoluteFillObject,
      borderRadius: 10,
      backgroundColor: C.surface + "CC",
      alignItems: "center",
      justifyContent: "flex-start",
      paddingTop: 56,
      zIndex: 2,
    },
    tableLoaderCard: {
      flexDirection: "row",
      alignItems: "center",
      gap: 12,
      maxWidth: "90%",
      paddingHorizontal: 18,
      paddingVertical: 12,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: C.border,
      backgroundColor: C.surface,
      shadowColor: "#000",
      shadowOpacity: 0.12,
      shadowRadius: 12,
      shadowOffset: { width: 0, height: 4 },
      elevation: 4,
    },
    tableLoaderTitle: { fontSize: 14, fontWeight: "800", color: C.text },
    tableLoaderSub: { fontSize: 12, color: C.textMuted, marginTop: 1 },
    table: {
      flexGrow: 1,
      borderWidth: 1,
      borderColor: C.border,
      borderRadius: 10,
      overflow: "hidden",
    },
    tr: { flexDirection: "row", alignItems: "center", paddingHorizontal: 8 },
    thRow: {
      minHeight: 44,
      borderBottomWidth: 1,
      borderBottomColor: C.border,
      backgroundColor: C.surfaceAlt,
    },
    bodyRow: {
      minHeight: 48,
      borderBottomWidth: 1,
      borderBottomColor: C.border,
    },
    th: {
      fontSize: 11,
      fontWeight: "800",
      color: C.textMuted,
      textTransform: "uppercase",
      letterSpacing: 0.4,
      paddingHorizontal: 6,
    },
    td: { fontSize: 13, color: C.text, paddingHorizontal: 6 },
    // Extra width on wide screens goes to Location, then State and Item name
    grow: { flexGrow: 1 },
    grow2: { flexGrow: 2 },
    locCell: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: 4,
      paddingHorizontal: 6,
      paddingVertical: 8,
    },
    locChip: {
      paddingHorizontal: 7,
      paddingVertical: 2,
      borderRadius: 6,
      backgroundColor: C.surfaceAlt,
      borderWidth: 1,
      borderColor: C.border,
    },
    locChipText: { fontSize: 12, fontWeight: "600", color: C.text },
    right: { textAlign: "right" },
    center: { textAlign: "center" },
    emptyTable: {
      padding: 24,
      fontSize: 13,
      color: C.textMuted,
      textAlign: "center",
    },
    checkCell: { alignItems: "center", justifyContent: "center", height: 40 },
    checkbox: {
      width: 18,
      height: 18,
      borderRadius: 4,
      borderWidth: 1.5,
      borderColor: C.textMuted,
      justifyContent: "center",
      alignItems: "center",
    },
    checkboxOn: { backgroundColor: C.accent, borderColor: C.accent },
    cellWrap: { paddingHorizontal: 8, paddingVertical: 6 },
    cellInput: {
      height: 32,
      borderRadius: 6,
      backgroundColor: C.cell,
      paddingHorizontal: 8,
      paddingVertical: 0,
      fontSize: 13,
      color: C.text,
      textAlign: "center",
      borderWidth: 1,
      borderColor: "transparent",
    },
    priceInput: { textAlign: "right", paddingRight: 12 },
    cellNew: { backgroundColor: C.warningBg, borderColor: C.warning },
    cellError: { borderColor: C.danger },
    uomBtn: {
      height: 32,
      borderRadius: 6,
      backgroundColor: C.cell,
      borderWidth: 1,
      borderColor: "transparent",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
    },
    uomText: { fontSize: 13, color: C.text },

    newPriceNote: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
      marginTop: 10,
    },
    newPriceSwatch: {
      width: 14,
      height: 10,
      borderRadius: 2,
      backgroundColor: C.warningBg,
      borderWidth: 1,
      borderColor: C.warning,
    },
    newPriceNoteText: { flex: 1, fontSize: 12, color: C.textMid },

    // Totals
    totals: {
      alignSelf: "flex-end",
      minWidth: 300,
      marginTop: 18,
      padding: 16,
      gap: 8,
      borderRadius: 12,
      backgroundColor: C.surfaceAlt,
    },
    totalRow: {
      flexDirection: "row",
      alignItems: "baseline",
      justifyContent: "space-between",
      gap: 24,
    },
    totalLabel: { fontSize: 14, fontWeight: "600", color: C.textMid },
    totalValue: { fontSize: 15, fontWeight: "700", color: C.text },
    grandRow: {
      borderTopWidth: 1,
      borderTopColor: C.border,
      paddingTop: 10,
      marginTop: 2,
    },
    grandValue: { fontSize: 20, fontWeight: "800", color: C.accent },
  });

const createSRP = (C: Tokens) =>
  StyleSheet.create({
    backdrop: { flex: 1, backgroundColor: C.overlay },
    backdropCenter: {
      justifyContent: "center",
      alignItems: "center",
      padding: 24,
    },
    backdropBottom: { justifyContent: "flex-end" },
    panel: { backgroundColor: C.surface },
    dialog: {
      width: "100%",
      maxHeight: "80%",
      borderRadius: 16,
      padding: 20,
      borderWidth: 1,
      borderColor: C.border,
      shadowColor: "#000",
      shadowOpacity: 0.18,
      shadowRadius: 24,
      shadowOffset: { width: 0, height: 12 },
      elevation: 8,
    },
    sheet: {
      borderTopLeftRadius: 20,
      borderTopRightRadius: 20,
      paddingHorizontal: 20,
      paddingTop: 12,
      paddingBottom: 24,
      maxHeight: "80%",
    },
    handle: {
      width: 36,
      height: 4,
      borderRadius: 2,
      backgroundColor: C.border,
      alignSelf: "center",
      marginBottom: 12,
    },
    titleRow: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 10,
    },
    title: { fontSize: 16, fontWeight: "800", color: C.text },
    closeBtn: {
      width: 32,
      height: 32,
      borderRadius: 8,
      justifyContent: "center",
      alignItems: "center",
    },
    searchWrap: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
      backgroundColor: C.field,
      borderRadius: 8,
      paddingHorizontal: 12,
      height: 40,
      marginBottom: 8,
    },
    searchInput: { flex: 1, fontSize: 14, color: C.text, paddingVertical: 0 },
    option: {
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
      paddingVertical: 11,
      paddingHorizontal: 10,
      borderRadius: 8,
    },
    optionHover: { backgroundColor: C.surfaceAlt },
    optionActive: { backgroundColor: C.accent + "12" },
    optionText: { fontSize: 14, fontWeight: "600", color: C.text },
    optionSub: { fontSize: 11, color: C.textMuted, marginTop: 2 },
    empty: {
      fontSize: 13,
      color: C.textMuted,
      textAlign: "center",
      paddingVertical: 28,
    },
  });

const createK = (C: Tokens) =>
  StyleSheet.create({
    monthRow: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 10,
    },
    navBtn: {
      width: 36,
      height: 36,
      borderRadius: 8,
      justifyContent: "center",
      alignItems: "center",
    },
    monthText: { fontSize: 15, fontWeight: "800", color: C.text },
    monthCenter: { flexDirection: "row", alignItems: "center", gap: 8 },
    dayFullText: {
      color: C.danger,
      opacity: 0.55,
      textDecorationLine: "line-through",
    },
    fullTag: {
      fontSize: 8,
      fontWeight: "800",
      color: C.danger,
      textTransform: "uppercase",
      marginTop: -1,
    },
    legend: { flexDirection: "row", alignItems: "center", gap: 4 },
    legendDot: {
      width: 8,
      height: 8,
      borderRadius: 4,
      backgroundColor: C.danger,
      opacity: 0.6,
    },
    grid: { flexDirection: "row", flexWrap: "wrap" },
    weekday: {
      width: `${100 / 7}%`,
      textAlign: "center",
      fontSize: 11,
      fontWeight: "800",
      color: C.textMuted,
      textTransform: "uppercase",
      paddingVertical: 6,
    },
    cell: { width: `${100 / 7}%`, aspectRatio: 1, padding: 2 },
    day: {
      flex: 1,
      borderRadius: 999,
      justifyContent: "center",
      alignItems: "center",
    },
    today: { borderWidth: 1, borderColor: C.accent + "66" },
    daySelected: { backgroundColor: C.accent },
    dayText: { fontSize: 14, fontWeight: "600", color: C.text },
    dayDisabled: {
      color: C.textMuted,
      opacity: 0.4,
      textDecorationLine: "line-through",
    },
    footer: {
      flexDirection: "row",
      alignItems: "center",
      gap: 6,
      marginTop: 10,
      paddingTop: 10,
      borderTopWidth: 1,
      borderTopColor: C.border,
    },
    footerText: { fontSize: 12, color: C.textMuted, flex: 1 },
  });

const createL = (C: Tokens) =>
  StyleSheet.create({
    overlay: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: C.surfaceAlt,
      justifyContent: "center",
      alignItems: "center",
      padding: 24,
    },
    card: {
      width: "100%",
      maxWidth: 360,
      backgroundColor: C.surface,
      borderRadius: 20,
      borderWidth: 1,
      borderColor: C.border,
      padding: 24,
      alignItems: "center",
      gap: 10,
    },
    iconWrap: {
      width: 52,
      height: 52,
      borderRadius: 16,
      justifyContent: "center",
      alignItems: "center",
    },
    title: { fontSize: 17, fontWeight: "800", color: C.text, marginTop: 6 },
    sub: {
      fontSize: 13,
      color: C.textMuted,
      textAlign: "center",
      lineHeight: 19,
    },
    steps: {
      alignSelf: "stretch",
      marginTop: 8,
      backgroundColor: C.surfaceAlt,
      borderRadius: 12,
      padding: 12,
      gap: 10,
    },
    step: {
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
      minHeight: 20,
    },
    stepText: { fontSize: 13, fontWeight: "600", color: C.textMuted },
    actions: {
      flexDirection: "row",
      gap: 10,
      alignSelf: "stretch",
      marginTop: 8,
    },
    secondaryBtn: {
      flex: 1,
      height: 44,
      borderRadius: 12,
      borderWidth: 1.5,
      borderColor: C.border,
      justifyContent: "center",
      alignItems: "center",
    },
    secondaryText: { fontSize: 14, fontWeight: "700", color: C.textMid },
    primaryBtn: {
      flex: 1,
      height: 44,
      borderRadius: 12,
      backgroundColor: C.accent,
      flexDirection: "row",
      gap: 6,
      justifyContent: "center",
      alignItems: "center",
    },
    primaryText: { fontSize: 14, fontWeight: "800", color: "#fff" },
  });
